import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Plus, Trash2 } from "lucide-react";

// Shared multi-range date-price editor used by the Global / Group / User
// pricing screens. A row may now hold ANY number of date ranges, with one hard
// rule: ranges in the same row must be fully disjoint — no shared start/end
// date and no overlap of any day. The pickers enforce this while editing
// (other ranges' days are disabled, and the "To" picker is capped at the day
// before the next range starts, which also blocks the enclosing case), and
// `rangesOverlap` is the belt-and-suspenders guard used on save.

const PRICE_COLS = [
  { key: "adultPrice", label: "Adult" },
  { key: "adultPrimePrice", label: "Adult (Prime)" },
  { key: "childPrice", label: "Child" },
  { key: "childPrimePrice", label: "Child (Prime)" },
  { key: "infantPrice", label: "Infant" },
  { key: "infantPrimePrice", label: "Infant (Prime)" },
];

const toYMD = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
const toDate = (s) => (s ? new Date(s) : null);
const nn = (v) => String(v ?? "").replace(/-/g, "");
// Today at midnight — the earliest selectable date (no past dates).
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const inAnyInterval = (date, intervals) =>
  (intervals || []).some((iv) => iv.start && iv.end && date >= iv.start && date <= iv.end);

// Colour each calendar day inline (reliable regardless of CSS scope): days before
// the earliest selectable day (minYMD) or falling inside another range's excluded
// interval are muted, the selected day is white, all other selectable days dark.
const renderDay = (selectedYMD, minYMD, excluded) => (day, date) => {
  const ymd = toYMD(date);
  const isDisabled = (!!minYMD && ymd < minYMD) || inAnyInterval(date, excluded);
  const isSelected = selectedYMD && ymd === selectedYMD;
  return (
    <span style={{ color: isDisabled ? "#c2c8d2" : isSelected ? "#fff" : "#1b2439" }}>{day}</span>
  );
};

const blankRange = () => ({
  startDate: "", endDate: "",
  adultPrice: "", adultPrimePrice: "", childPrice: "",
  childPrimePrice: "", infantPrice: "", infantPrimePrice: "",
});

// A day offset from a YYYY-MM-DD string, returned as a Date (matches toDate's
// parsing so the calendar bounds stay consistent with the rest of the app).
const addDays = (ymd, delta) => {
  const d = toDate(ymd);
  if (!d) return undefined;
  d.setDate(d.getDate() + delta);
  return d;
};

// True if any two dated ranges share a day (or an endpoint). YYYY-MM-DD strings
// are zero-padded, so plain string comparison orders them correctly.
export const rangesOverlap = (ranges) => {
  const rs = (ranges || [])
    .filter((r) => r.startDate && r.endDate)
    .map((r) => ({ s: r.startDate, e: r.endDate }))
    .sort((a, b) => (a.s < b.s ? -1 : a.s > b.s ? 1 : 0));
  for (let i = 1; i < rs.length; i++) {
    if (rs[i].s <= rs[i - 1].e) return true;
  }
  return false;
};

const _has = (v) => v !== "" && v !== null && v !== undefined;

// A date range is "started" once any date or price is filled. Any started range
// MUST be complete: both From and To, and an Adult price. Returns the first
// error message found, or null when every range is either empty or complete.
export const firstIncompleteRange = (ranges) => {
  for (const dr of ranges || []) {
    const started =
      !!dr.startDate ||
      !!dr.endDate ||
      ["adultPrice", "adultPrimePrice", "childPrice", "childPrimePrice", "infantPrice", "infantPrimePrice"].some(
        (k) => _has(dr[k])
      );
    if (!started) continue;
    if (!dr.startDate) return "Select a start (From) date for every date range.";
    if (!dr.endDate) return "Select an end (To) date for every date range.";
    if (!_has(dr.adultPrice)) return "Adult price is required for every date range.";
  }
  return null;
};

const DateRangesEditor = ({ ranges = [], onChange, hint }) => {
  // Always show at least one editable range so an expanded row is never blank.
  const view = ranges && ranges.length ? ranges : [blankRange()];

  const addRange = () => onChange([...(ranges && ranges.length ? ranges : []), blankRange()]);
  const removeRange = (idx) => onChange(view.filter((_, j) => j !== idx));
  const patch = (idx, p) => onChange(view.map((r, j) => (j === idx ? { ...r, ...p } : r)));

  // The other dated ranges this range must stay clear of.
  const othersOf = (idx) => view.filter((r, j) => j !== idx && r.startDate && r.endDate);
  const excludeIntervals = (idx) =>
    othersOf(idx).map((r) => ({ start: toDate(r.startDate), end: toDate(r.endDate) }));
  // Earliest other-range start strictly after `startYMD` — caps the "To" picker.
  const nextStartAfter = (idx, startYMD) => {
    const starts = othersOf(idx).map((r) => r.startDate).filter((s) => s > startYMD).sort();
    return starts.length ? starts[0] : null;
  };

  // Setting a new "From": drop the existing "To" if it is now invalid (before the
  // new start, or crossing into the next range).
  const setStart = (idx, date) => {
    const startYMD = toYMD(date);
    const cur = view[idx] || blankRange();
    let endDate = cur.endDate;
    if (startYMD && endDate) {
      const cap = nextStartAfter(idx, startYMD);
      if (endDate < startYMD || (cap && endDate >= cap)) endDate = "";
    }
    patch(idx, { startDate: startYMD, endDate });
  };

  return (
    <div className="dr">
      <div className="dr-head">
        Date-range prices
        <span className="dr-hint">{hint || "each range overrides the price for the travel dates it covers"}</span>
      </div>

      {view.map((rg, idx) => {
        const cap = rg.startDate ? nextStartAfter(idx, rg.startDate) : null;
        const maxEnd = cap ? addDays(cap, -1) : undefined;
        return (
          <div className="drr" key={idx}>
            <div className="drr-top">
              <span className="drr-badge">Range {idx + 1}</span>
              <button className="drr-del" onClick={() => removeRange(idx)}>
                <Trash2 size={13} /> Remove
              </button>
            </div>
            <div className="dr-form">
              <div className="dr-f">
                <label>From</label>
                <DatePicker
                  selected={toDate(rg.startDate)}
                  onChange={(date) => setStart(idx, date)}
                  selectsStart
                  startDate={toDate(rg.startDate)}
                  endDate={toDate(rg.endDate)}
                  minDate={startOfToday()}
                  excludeDateIntervals={excludeIntervals(idx)}
                  renderDayContents={renderDay(rg.startDate, toYMD(startOfToday()), excludeIntervals(idx))}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="Select date"
                  className="inp dp"
                  portalId="rdp-portal"
                  isClearable
                />
              </div>
              <div className="dr-f">
                <label>To</label>
                <DatePicker
                  selected={toDate(rg.endDate)}
                  onChange={(date) => patch(idx, { endDate: toYMD(date) })}
                  selectsEnd
                  startDate={toDate(rg.startDate)}
                  endDate={toDate(rg.endDate)}
                  minDate={toDate(rg.startDate) || startOfToday()}
                  maxDate={maxEnd}
                  excludeDateIntervals={excludeIntervals(idx)}
                  renderDayContents={renderDay(rg.endDate, rg.startDate || toYMD(startOfToday()), excludeIntervals(idx))}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="Select date"
                  className="inp dp"
                  portalId="rdp-portal"
                  isClearable
                  disabled={!rg.startDate}
                />
              </div>
              {PRICE_COLS.map((c) => (
                <div className="dr-f" key={c.key}>
                  <label>{c.label}</label>
                  <input
                    className="inp price"
                    type="number"
                    min={0}
                    value={rg[c.key] ?? ""}
                    onChange={(e) => patch(idx, { [c.key]: nn(e.target.value) })}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <button className="dr-add" onClick={addRange}>
        <Plus size={14} /> Add date range
      </button>
    </div>
  );
};

export default DateRangesEditor;
